<template>
  <modal
    ref="solverModal"
    title="Solve shortfall"
    idKey="solverModal"
    key="solverModal"
    :close-button-aria-label="modalCloseButtonAriaLabel"
  >
    <template v-if="isEvaluating">
      <div class="row">
        <div class="col">
          <p>Evaluating...</p>
        </div>
      </div>
      <div class="row">
        <div class="col text-center">
          <div class="spinner-border" role="status">
            <span class="visually-hidden">Loading...</span>
          </div>
        </div>
      </div>
    </template>
    <template v-else>
      <div class="row">
        <div class="col">
          <template v-if="isViable">
            <p v-for="solutionLine, i in solutionText" :key="i">{{ solutionLine }}</p>
            <p>{{ confirmSolutionText }}</p>
          </template>
          <template v-else>
            <p v-for="(text, i) in solveFailureInfo.text" :key="i">{{ text }}</p>
            <ul>
              <li v-for="(action, i) in solveFailureInfo.actions" :key="i">{{ action }}</li>
            </ul>
          </template>
        </div>
      </div>
    </template>
    <div class="d-grid gap-2 d-md-flex justify-content-md-end mt-4">
      <button
        type="button"
        v-if="showYesButton"
        :disabled="!yesBtnEnabled"
        class="btn px-5 btn-secondary"
        aria-label="Yes"
        @click="onYesClicked"
      >
        Yes
        <span
          v-if="implementingSolution"
          class="spinner-border spinner-border-sm mb-1"
          role="status"
          aria-hidden="true"
        ></span>
      </button>
      <button
        type="button"
        class="btn px-5 btn-secondary"
        aria-label="No"
        @click="onNoClicked"
      >
        {{ noButtonText }}
      </button>
    </div>
  </modal>
</template>

<script lang="ts">
import { defineComponent, ref } from "vue";
import { app } from "@/guiide/ts/app";
import Modal from "@/guiide/components/common/Modal.vue";
import SolveType from "@/guiide/ts/solve/solveType";
import TakeLessResponse from "@/guiide/ts/solve/takeLessResponse";
import PayMoreResponse from "@/guiide/ts/solve/payMoreResponse";
import EquityReleaseResponse from "@/guiide/ts/solve/equityReleaseResponse";
import { Configuration } from "@/guiide/ts/configuration/configuration";
import ScenarioType from "@/guiide/ts/scenarioType";
import { formatter } from "@/shared/ts/formatter";

type SolveFailureInfo = {
  text: string[]
  actions: string[]
}

export default defineComponent({
  name: "ShortfallSolver",
  components: {
    Modal
  },
  emits: ["closing"],
  data() {
    return {
      solveType: SolveType.TakeLess,
      scenario: ScenarioType.AccordingToPlan,
      isEvaluating: true,
      yesBtnEnabled: true,
      takeLessResponse: {
        isViable: false,
        reducedByPercent: 0
      } as TakeLessResponse,
      payMoreResponse: {
        isViable: false,
        monthlyContribution: 0
      } as PayMoreResponse,
      equityReleaseResponse: {
        isViable: false,
        equityRelease: 0
      } as EquityReleaseResponse,
      implementingSolution: false,
      SolveType
    };
  },
  setup() {
    const solverModal = ref(null);
    return {
      solverModal
    };
  },
  computed: {
    scenarioText: function() {
      switch (this.scenario) {
        case ScenarioType.AccordingToPlan:
          return "everything turns out as expected";
        case ScenarioType.LiveThreeYearsLonger:
          return "you live longer";
        case ScenarioType.PotsFallTwentyPercent:
          return "the market crashes";
        case ScenarioType.NeedExtraMoneyForLastTwoYears:
          return "you need some care in later life";
        case ScenarioType.LowerGrowth:
          return "growth is lower";
        default:
          throw Error("Unknown scenario type.");
      }
    },
    isViable: function() {
      return (
        (this.solveType == SolveType.TakeLess && this.takeLessResponse.isViable) ||
        (this.solveType == SolveType.PayMore && this.payMoreResponse.isViable) ||
        (this.solveType == SolveType.EquityRelease && this.equityReleaseResponse.isViable)
      );
    },
    solveFailureInfo: function(): SolveFailureInfo {
      const firstTextLine = this.solveType == SolveType.TakeLess
        ? `The only way to make your plan work if ${this.scenarioText} is to reduce what you want by more than 50%.`
        : `Even if you increase your total contribution to £3,000 per month you would be expected to run out of money in retirement if ${this.scenarioText}.`;

      return {
        text: [
          firstTextLine,
          "You may wish to try any of the following if you haven't already done so:"
        ],
        actions: [
          "reduce your income to essential parts only in the wants page, come back and increase your contributions using the solve"
          + " button to see if your income can be met with an affordable contribution",
          "consider not taking 25% of your defined contribution pension pots as cash at retirement and spending this, if you are currently doing so",
          "consider if there is anything else you can put towards your retirement income",
          "consider if you are able to retire a little later"
        ]
      };
    },
    solutionText: function(): string[] {
      switch (this.solveType) {
        case SolveType.TakeLess:
          return (
            [`If you reduce the original amount you want by ${this.takeLessResponse.reducedByPercent}%, your pensions and savings` +
            ` pots would be expected to last throughout your retirement if ${this.scenarioText}.`]
          );
        case SolveType.PayMore:
          return (
            [`If you increase your pension payments to £${this.payMoreResponse.monthlyContribution} a month, your` +
            ` pensions and savings pots would be expected to last throughout your retirement if ${this.scenarioText}.`]
          );
        case SolveType.EquityRelease: {
          const formattedEquityAmount = formatter.formatCurrency(this.equityReleaseResponse.equityRelease);
          return [`If you were able to secure ${formattedEquityAmount} of Home Equity (via downsizing or Equity Release) to help towards your retirement income, this would remove any shortfalls.`]
        }
        default:
          return [];
      }
    },
    confirmSolutionText: function() {
      switch (this.solveType) {
        case SolveType.TakeLess:
          return "Do you want to reduce what you want by this amount?";
        case SolveType.PayMore:
          return (this.$store.state.configuration as Configuration).solveIncPaymentsConfirmText;
        case SolveType.EquityRelease:
          return "Would you like to add this to your plan?";
        default:
          return "";
      }
    },
    showYesButton: function() {
      return !this.isEvaluating && this.isViable;
    },
    modalCloseButtonAriaLabel: function() {
      if (this.isEvaluating) {
        return "Close button for the solve shortfall popup which is currently evaluating - please wait.";
      } else {
        const finishedEvalIntro = "The evaluation has completed.";

        if (this.isViable) {
          let content = "";

          for (const line of this.solutionText) {
            content += " " + line;
          }

          const closeDialogAdvice = "Tab to Yes and No buttons, or press space or enter to close the popup.";
          return `${finishedEvalIntro} A viable solution has been found. ${content} ${this.confirmSolutionText} ${closeDialogAdvice}`;
        } else {
          const content = new Array<string>();
          const failureInfo = this.solveFailureInfo;

          for (const textItem of failureInfo.text) {
            content.push(`${textItem} `);
          }

          let adviceItemNumber = 1;
          for (const action of failureInfo.actions) {
            content.push(` ${adviceItemNumber} - ${action}.`);
            adviceItemNumber++;
          }

          const response = content.join("");
          const closeDialogAdvice = "Press space or enter to close the popup.";
          return `${finishedEvalIntro} A viable solution was not found. ${response} ${closeDialogAdvice}`;
        }
      }
    },
    noButtonText: function() {
      if (this.isEvaluating) {
        return "Close";
      } else {
        return this.isViable ? "No" : "Close";
      }
    }
  },
  methods: {
    async checkCouldSolveByTakeLess(scenarioType: ScenarioType) {
      this.initialise(SolveType.TakeLess, scenarioType);
      this.solverModal.showModal();
      this.takeLessResponse = await app.checkIfCanSolveByTakingLess(this.scenario);
      this.isEvaluating = false;
    },
    async checkCouldSolveByPayingMore(scenarioType: ScenarioType) {
      this.initialise(SolveType.PayMore, scenarioType);
      this.solverModal.showModal();
      this.payMoreResponse = await app.checkIfCanSolveByPayingMore(this.scenario);
      this.isEvaluating = false;
    },
    async checkCouldSolveByEquityRelease(scenarioType: ScenarioType) {
      this.initialise(SolveType.EquityRelease, scenarioType);
      this.solverModal.showModal();
      this.equityReleaseResponse = await app.checkIfCanSolveByEquityRelease(this.scenario);
      this.isEvaluating = false;
    },
    async onYesClicked() {
      this.implementingSolution = true;
      this.yesBtnEnabled = false;

      switch (this.solveType) {
        case SolveType.TakeLess: {
          await app.solveByReducingIncome(this.takeLessResponse.reducedByPercent);
          break;
        }
        case SolveType.PayMore: {
          await app.solveByIncreasingPayments(this.payMoreResponse.monthlyContribution);
          break;
        }
        case SolveType.EquityRelease: {
          await app.solveByReleasingEquity(this.equityReleaseResponse.equityRelease);
          break;
        }
      }

      this.solverModal.closeModal();
      this.$emit("closing", { scenario: this.scenario, implemented: true });
    },
    async onNoClicked() {
      this.solverModal.closeModal();
      this.$emit("closing", { scenario: this.scenario, implemented: false });
    },
    initialise(solveType: SolveType, scenarioType: ScenarioType) {
      this.solveType = solveType;
      this.scenario = scenarioType;
      this.isEvaluating = true;
      this.yesBtnEnabled = true;
      this.implementingSolution = false;
    }
  }
});
</script>
